import { create } from 'zustand';
import { getSsoProviders, getSsoConfiguration } from '../services/ssoService';

/**
 * @typedef {Object} SsoProvider
 * @property {number} id - Provider ID
 * @property {string} code - Provider code (e.g., "GOOGLE", "MICROSOFT")
 * @property {string} name - Provider display name
 * @property {boolean} isActive - Whether the provider is active
 */

const defaultState = {
  providers: [],
  configuration: {
    externalAuthenticationProviderId: null,
    idpAlias: null,
    subdomain: null
  },
  hasLoaded: false
};

export const useSsoStore = create((set) => ({
  ...defaultState,
  isLoadingProviders: false,
  isLoadingConfiguration: false,
  providerError: null,
  configurationError: null,

  getSsoProviders: async () => {
    try {
      set({ isLoadingProviders: true, providerError: null });

      const result = await getSsoProviders();
      if (result.success) {
        const providers = result.data?.providers;
        if (!providers) {
          console.warn('SSO providers API returned unexpected shape: missing providers array', result.data);
        }
        set({
          providers: providers || [],
          hasLoaded: true
        });
      } else {
        set({
          providerError: result.message || 'Failed to load SSO providers'
        });
      }
    } catch (error) {
      set({
        providerError: error.message || 'Failed to fetch SSO providers'
      });
    } finally {
      set({ isLoadingProviders: false });
    }
  },

  getSsoConfiguration: async () => {
    try {
      set({ isLoadingConfiguration: true, configurationError: null });

      const result = await getSsoConfiguration();
      if (result.success) {
        set({
          configuration: result.data || defaultState.configuration,
          hasLoaded: true
        });
      } else {
        set({
          configurationError: result.message || 'Failed to load SSO configuration'
        });
      }
    } catch (error) {
      set({
        configurationError: error.message || 'Failed to fetch SSO configuration'
      });
    } finally {
      set({ isLoadingConfiguration: false });
    }
  },

  reset: () => set({
    ...defaultState,
    isLoading: false,
    isLoadingProviders: false,
    isLoadingConfiguration: false,
    providerError: null,
    configurationError: null
  })
}));
